import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  ObservationToolDocumentControlSystemTypes,
  SMEDisciplineInObservationViewModel,
} from 'src/app/shared/models/autogenerated';
import { TimeSincePipe } from 'src/app/shared/pipes/time-since.pipe';
import { selectRouteNestedParam } from 'src/app/shared/store/shared.selectors';
import { nameof } from 'src/app/shared/utils/nameof';
import { sortBy } from 'src/app/shared/utils/sort';
import {
  ObservationToolDashboardPageViewState,
  ObservationToolConfigState,
  ObservationToolState,
  SMEHoursState,
} from './observation-tool.reducer';

export const selectObservationToolConfigFeature =
  createFeatureSelector<ObservationToolConfigState>('observationToolConfig');

export const selectObservationToolDashboardPageViewFeature =
  createFeatureSelector<ObservationToolDashboardPageViewState>(
    'observationToolDashboardPageView'
  );

export const selectObservationToolFeature =
  createFeatureSelector<ObservationToolState>('observation-tool');

export const selectObservationToolSMEHoursFeature =
  createFeatureSelector<SMEHoursState>('sme-hours');

export const selectObservationTool = createSelector(
  selectObservationToolFeature,
  (state) => state.observationTool
);

export const selectObservationToolListQuickFilter = createSelector(
  selectObservationToolConfigFeature,
  selectRouteNestedParam('phaseId'),
  (state, phaseId) => state.observationToolListConfig?.[phaseId]?.quickFilter
);

export const selectObservationToolFiltered = createSelector(
  selectObservationTool,
  selectObservationToolListQuickFilter,
  (projectDeliverables, quickFilter) => {
    const quickFilterForComparison = quickFilter?.toLowerCase() ?? '';
    const timeSincePipe = new TimeSincePipe();

    return projectDeliverables?.filter(
      (item) =>
        timeSincePipe
          .transform(item.lastActivity)
          ?.toLowerCase()
          .includes(quickFilterForComparison) ||
        item.projectDeliverable.name
          .toLowerCase()
          .includes(quickFilterForComparison) ||
        item.projectDeliverable.type
          ?.toLowerCase()
          .includes(quickFilterForComparison) ||
        item.documents.find(
          (document) =>
            document.title?.toLowerCase().includes(quickFilterForComparison) ||
            document.documentID
              ?.toLowerCase()
              .includes(quickFilterForComparison)
        )
    );
  }
);

export const selectObservationToolDashboardObservations = createSelector(
  selectObservationToolFeature,
  (state) => state.observationToolDashboardObservations
);

export const selectObservationToolDashboardObservationsListQuickFilter =
  createSelector(
    selectObservationToolConfigFeature,
    selectRouteNestedParam('phaseId'),
    (state, phaseId) =>
      state.observationToolListConfig?.[phaseId]?.observationsQuickFilter
  );

export const selectObservationToolDashboardObservationsFiltered =
  createSelector(
    selectObservationToolDashboardObservations,
    selectObservationToolDashboardObservationsListQuickFilter,
    (observations, quickFilter) => {
      const quickFilterForComparison = quickFilter?.toLowerCase() ?? '';

      return observations?.filter(
        (item) =>
          item.observationNumber
            ?.toString()
            .includes(quickFilterForComparison) ||
          item.title?.toLowerCase().includes(quickFilterForComparison) ||
          item.status.toLowerCase().includes(quickFilterForComparison) ||
          item.standing?.toLowerCase().includes(quickFilterForComparison) ||
          item.daComments?.toLowerCase().includes(quickFilterForComparison)
      );
    }
  );

export const selectObservationToolDashboardChecks = createSelector(
  selectObservationToolFeature,
  (state) => state.observationToolDashboardChecks
);

export const selectObservationToolChecksListQuickFilter = createSelector(
  selectObservationToolConfigFeature,
  selectRouteNestedParam('phaseId'),
  (state, phaseId) =>
    state.observationToolListConfig?.[phaseId]?.checksQuickFilter
);

export const selectObservationToolChecksFiltered = createSelector(
  selectObservationToolDashboardChecks,
  selectObservationToolChecksListQuickFilter,
  (checks, quickFilter) => {
    const quickFilterForComparison = quickFilter?.toLowerCase() ?? '';

    return checks?.filter(
      (item) =>
        item.scopeOfReview?.toString().includes(quickFilterForComparison) ||
        item.type?.toLowerCase().includes(quickFilterForComparison) ||
        item.deliverableKeywords
          ?.toLowerCase()
          .includes(quickFilterForComparison) ||
        item.owner?.toLowerCase().includes(quickFilterForComparison) ||
        item.smeDiscipline?.disciplineCode
          ?.toLowerCase()
          .includes(quickFilterForComparison) ||
        item.smeDiscipline?.name
          ?.toLowerCase()
          .includes(quickFilterForComparison)
    );
  }
);

export const selectObservationToolCheckDetails = createSelector(
  selectObservationToolFeature,
  (state) => state.currentObservationToolCheck
);

export const selectObservationToolDashboardPageView = createSelector(
  selectObservationToolDashboardPageViewFeature,
  selectRouteNestedParam('phaseId'),
  (state, phaseId) => state.observationToolDashboardPageView?.[phaseId]
);

export const selectObservationToolItemsDisplay = createSelector(
  selectObservationToolFeature,
  (state) => state.observationToolItemsDisplay
);

export const selectObservationToolDashboardObservationsItemsDisplay =
  createSelector(
    selectObservationToolFeature,
    (state) => state.observationToolDashboardObservationsItemsDisplay
  );

export const selectObservationToolChecksItemsDisplay = createSelector(
  selectObservationToolFeature,
  (state) => state.observationToolChecksItemsDisplay
);

export const selectObservationToolDetails = createSelector(
  selectObservationToolFeature,
  (state) => state.observationToolDetails
);

export const selectObservationToolDetailsDisplay = createSelector(
  selectObservationToolFeature,
  (state) => state.observationToolDetailsDisplay
);

export const selectCurrentSMEHoursModifier = createSelector(
  selectObservationToolFeature,
  (state) => state.smeHoursModifier
);

export const selectCurrentObservationToolObservation = createSelector(
  selectObservationToolFeature,
  (state) => state.currentObservation
);

export const selectObservationToolObservationForm = createSelector(
  selectObservationToolFeature,
  (state) => state.currentObservationForm
);

export const selectObservationToolObservationDocuments = createSelector(
  selectObservationToolFeature,
  selectCurrentObservationToolObservation,
  (state, currentObservation) => {
    const documents = [...state.currentObservationDocuments!];

    // add the document from current observation to the list if it does not contain
    if (
      currentObservation &&
      currentObservation.document &&
      !documents.find(
        (x) => x.documentID === currentObservation.document?.documentID
      )
    ) {
      documents.push(currentObservation.document!);
    }

    return documents.filter(
      (x) =>
        x.documentControlSystemType !=
        ObservationToolDocumentControlSystemTypes.SharePointFolder
    );
  }
);

export const selectReviewingDisciplinesForObservation = createSelector(
  selectObservationToolFeature,
  selectCurrentObservationToolObservation,
  (state, currentObservation) => {
    const disciplines = [
      ...state.currentObservationReviewingDisciplinesOptions!,
    ];

    // add the observation form discipline
    if (
      currentObservation &&
      !disciplines.find((x) => x.id === currentObservation.smeDiscipline?.id)
    ) {
      disciplines.push({
        ...currentObservation.smeDiscipline,
        isAssignedToCurrentUser: false,
      } as SMEDisciplineInObservationViewModel);
    }

    sortBy(
      disciplines,
      nameof<SMEDisciplineInObservationViewModel>('disciplineCode')
    );

    return disciplines;
  }
);

export const selectDisciplineForObservation = createSelector(
  selectObservationToolFeature,
  (state) => state.disciplinesWithCodesForObservation
);

export const selectRespondingToOptionsForObservation = createSelector(
  selectObservationToolFeature,
  (state) => state.respondingToOptionsForObservation
);
export const selectAreaSelectionOptionsForObservation = createSelector(
  selectObservationToolFeature,
  (state) => state.areaSelectionOptionsForObservation
);
export const selectObservationToolObservationDocument = createSelector(
  selectObservationToolFeature,
  selectObservationToolObservationDocuments,
  (state, documents) => {
    return documents.find(
      (doc) =>
        doc.title ===
        state.currentObservationForm?.controls.documentTitle?.value
    );
  }
);

export const selectIsDisableInitialObservationsTab = createSelector(
  selectObservationToolFeature,
  (state) => state.disableInitialObservationsTab
);

export const selectShowSMEHoursAsPercent = createSelector(
  selectObservationToolSMEHoursFeature,
  (state) => state.showSMEHoursAsPercent
);

export const selectDeliverableFlagForm = createSelector(
  selectObservationToolFeature,
  (state) => state.deliverableFlagForm
);

export const selectDeliverableNoteForm = createSelector(
  selectObservationToolFeature,
  (state) => state.deliverableNoteForm
);

export const selectCurrentNickname = createSelector(
  selectObservationToolFeature,
  (state) => state.currentChangedNickname
);

export const selectObservationToolMessages = createSelector(
  selectObservationToolFeature,
  (state) => state.currentObservation?.observationToolObservationMessages
);

export const selectObservationToolNawMessages = createSelector(
  selectObservationToolFeature,
  (state) => state.currentObservation?.observationToolObservationNawMessage
);

export const selectObservationToolCurrentChannel = createSelector(
  selectObservationToolFeature,
  (state) => state.currentObservationChannel
);

export const selectObservationToolCurrentMessageForm = createSelector(
  selectObservationToolFeature,
  (state) => state.currentObservationMessageForm
);

export const selectObservationToolCheckCurrentMessageForm = createSelector(
  selectObservationToolFeature,
  (state) => state.currentObservationToolCheckMessageForm
);

export const selectObservationToolMessageReturnTo = createSelector(
  selectObservationToolFeature,
  (state) => state.messageReturnedToRole
);

export const selectObservationToolNextActionWith = createSelector(
  selectObservationToolFeature,
  (state) => state.currentObservation?.nextActionWith
);

export const selectObservationToolObservationP3InformationalForm =
  createSelector(
    selectObservationToolFeature,
    (state) => state.currentObservationP3InformationalForm
  );

export const selectObservationToolObservationAttachments = createSelector(
  selectObservationToolFeature,
  (state) => state.currentObservation?.observationToolObservationAttachments
);

export const selectDADeliverableDetailsDialog = createSelector(
  selectObservationToolFeature,
  (state) => state.daDeliverableDetails
);

export const selectOTCheckAdditionalInfoForm = createSelector(
  selectObservationToolFeature,
  (state) => state.currentObservationToolCheckInfoForm
);

export const selectSMECommentsForm = createSelector(
  selectObservationToolFeature,
  (state) => state.currentSmeCommentsForm
);

export const selectObservationToolFinalObservation = createSelector(
  selectObservationToolFeature,
  (state) => state.currentObservationForm?.controls.finalObservation
);

export const selectObservationToolFinalConcern = createSelector(
  selectObservationToolFeature,
  (state) => state.currentObservationForm?.controls.finalConcern
);

export const selectObservationToolFinalRecommendation = createSelector(
  selectObservationToolFeature,
  (state) => state.currentObservationForm?.controls.finalRecommendation
);

export const selectObservationToolDashboardIndicators = createSelector(
  selectObservationToolFeature,
  (state) => state.observationToolDashboardIndicators
);

export const selectObservationToolDocumentsFilter = createSelector(
  selectObservationToolFeature,
  (state) => state.observationToolDocumentsFilter
);

export const selectDisciplinesInFilter = createSelector(
  selectObservationToolFeature,
  (state) => state.disciplinesInFilter
);

export const selectObservationToolDocumentsSort = createSelector(
  selectObservationToolFeature,
  (state) => state.observationToolDocumentsSort
);

export const selectDADeliverableForm = createSelector(
  selectObservationToolFeature,
  (state) => state.daDeliverableForm
);

export const selectAttentionItemEmailResult = createSelector(
  selectObservationToolFeature,
  (state) => state.attentionItemEmailResult
);

export const selectObservationToolCheckMessages = createSelector(
  selectObservationToolFeature,
  (state) => state.currentObservationToolCheck?.observationToolCheckMessages
);
